import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CheckCircle, XCircle, CalendarClock } from "lucide-react";
import clsx from "clsx";
import { AuthenticationService } from "@/api/auth";
import { useNavigate } from "react-router-dom";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { postAuthForgotPassword, postAuthConfirmForgotPassword } =
    AuthenticationService;
  const { toast } = useToast();
  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  const passwordValidations = {
    length: newPassword.length >= 8,
    upper: /[A-Z]/.test(newPassword),
    lower: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[^A-Za-z0-9]/.test(newPassword),
  };

  const allValid = Object.values(passwordValidations).every(Boolean);

  const renderCheck = (isValid: boolean) =>
    isValid ? (
      <CheckCircle className="text-green-600 w-4 h-4" />
    ) : (
      <XCircle className="text-red-500 w-4 h-4" />
    );

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate API call
      //   await new Promise((resolve) => setTimeout(resolve, 1000));
      const response: any = await postAuthForgotPassword({ email });
      if (response.statusCode === 200 || response.statusCode === 201) {
        toast({
          title: "OTP Sent",
          description:
            response.body.message || `A reset code has been sent to ${email}`,
        });
        setStep(2);
      } else {
        toast({
          variant: "destructive",
          title: "Error sending you OTP",
          description:
            response.body.message ||
            "Please check your credentials and try again.",
        });
      }
    } catch {
      toast({
        variant: "destructive",
        title: "Failed to send OTP",
        description: "Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast({
        title: "OTP resent",
        description: `A new OTP has been sent to ${email}`,
      });
    } catch {
      toast({
        variant: "destructive",
        title: "Failed to resend OTP",
      });
    } finally {
      setResending(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Passwords do not match",
      });
      return;
    }

    if (!allValid) {
      toast({
        variant: "destructive",
        title: "Invalid password",
        description: "Please meet all password requirements.",
      });
      return;
    }

    setIsSubmitting(true);
    const data = {
      email: email,
      code: otp,
      password: newPassword,
    };
    try {
      const response: any = await postAuthConfirmForgotPassword(data);
      if (response.statusCode === 200 || response.statusCode === 201) {
        toast({
          title: "OTP Sent",
          description:
            response.body.message ||
            "You may now log in with your new password.",
        });
        navigate("/signin");
      } else {
        toast({
          variant: "destructive",
          title: "Error sending you OTP",
          description:
            response.body.message ||
            "Please check your credentials and try again.",
        });
      }
    } catch {
      toast({
        variant: "destructive",
        title: "Reset failed",
        description: "Please check your OTP and try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="flex items-center mb-8">
        <CalendarClock className="h-8 w-8 text-cecure-700 mr-2" />
        <span className="text-2xl font-bold text-cecure-900 font-heading">
          CecureStream <span className="text-accent">Events</span>
        </span>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center text-2xl">
            {step === 1 ? "Forgot Password" : "Reset Password"}
          </CardTitle>
          <CardDescription className="text-center">
            {step === 1
              ? "Enter your email to receive a reset code"
              : "Enter the OTP and set a new password"}
          </CardDescription>
        </CardHeader>

        {step === 1 ? (
          <form onSubmit={handleRequestOtp}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="submit"
                className="w-full bg-cecure-700 hover:bg-cecure-800"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Send Reset Code"}
              </Button>
            </CardFooter>
          </form>
        ) : (
          <form onSubmit={handleResetPassword}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  readOnly
                  className="bg-gray-100 cursor-not-allowed"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="otp">OTP</Label>
                <Input
                  id="otp"
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="text-xs text-cecure-700 hover:underline mt-1"
                  onClick={handleRequestOtp}
                  disabled={resending}
                >
                  {resending ? "Resending..." : "Resend OTP"}
                </button>
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <ul className="text-sm mt-1 space-y-1">
                  <li className="flex items-center gap-2">
                    {renderCheck(passwordValidations.length)} At least 8
                    characters
                  </li>
                  <li className="flex items-center gap-2">
                    {renderCheck(passwordValidations.upper)} One uppercase
                    letter
                  </li>
                  <li className="flex items-center gap-2">
                    {renderCheck(passwordValidations.lower)} One lowercase
                    letter
                  </li>
                  <li className="flex items-center gap-2">
                    {renderCheck(passwordValidations.number)} One number
                  </li>
                  <li className="flex items-center gap-2">
                    {renderCheck(passwordValidations.special)} One special
                    character
                  </li>
                </ul>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="submit"
                className="w-full bg-cecure-700 hover:bg-cecure-800"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Resetting..." : "Reset Password"}
              </Button>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  );
};

export default ForgotPassword;
